


// ###############################################################

import React, { useEffect, useState } from "react";
import WorkspaceService from "services/workspace-service";
import GuidanceBubble from "layouts/guidance/components/GuidanceBubble"; // [NEW]
import CircularProgress from "@mui/material/CircularProgress";
import Card from "@mui/material/Card";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import IconButton from "@mui/material/IconButton";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import Typography from "@mui/material/Typography";
import Snackbar from "@mui/material/Snackbar";
import Grow from "@mui/material/Grow";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import ProfileSearch from "../../../social-stash/components/toolbar/ProfileSearch";
import SocialCard from "examples/Cards/SocialCard";

import ProfileService from "services/profile-service";
import SearchCompetitorModal from "./SearchCompetitorModal";

const CustomTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 400,
    textAlign: "left",
  },
});

const tooltipContent = (
  <div style={{ maxWidth: "350px", textAlign: "left" }}>
    <Typography variant="body2" gutterBottom>
      <strong>How to Use Competitor Selector:</strong>
    </Typography>
    <Typography variant="caption" display="block" gutterBottom>
      - View and manage saved <strong>competitor</strong> profiles. <br />
      - Select competitor profile to compare. <br />
      - Click â€œContinueâ€ to proceed to snapshot generation.
    </Typography>
    <Typography variant="body2" gutterBottom>
      <strong>Quick Actions:</strong>
    </Typography>
    <Typography variant="caption" display="block">
      - Use the search bar to filter profiles. <br />
      - Click â€œAdd Profileâ€ to link a new competitor page. <br />
    </Typography>
  </div>
);

function SelectSecondaryProfile({
  selectedProfiles = [],
  onSelect,
  filterPlatform, // âœ… platform-based filtering
  excludeIds = [], // âœ… exclude primary profile(s)
  maxSelection,
  isLiveDemo, // [NEW]
  isStep1,    // [NEW]
  isStep2 = false, // [NEW]
  isDemoHighlight = false // [NEW]
}) {
  const [competitorProfiles, setCompetitorProfiles] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "" });

  const [toolbarTop, setToolbarTop] = useState(120);
  const [isTransparent, setIsTransparent] = useState(true);


  // âœ… Toolbar positioning + scroll shadow
  useEffect(() => {
    const updateToolbarTop = () => {
      const navbar = document.querySelector("header.MuiAppBar-root");
      const stepper = document.querySelector(".MuiStepper-root");
      if (navbar && stepper) {
        const totalTop = navbar.offsetHeight + stepper.offsetHeight + 8;
        setToolbarTop(totalTop);
      } else if (navbar) {
        setToolbarTop(navbar.offsetHeight + 60);
      }
    };

    updateToolbarTop();
    const resizeObserver = new ResizeObserver(updateToolbarTop);
    resizeObserver.observe(document.body);

    const scrollHandler = () => setIsTransparent(window.scrollY === 0);
    window.addEventListener("resize", updateToolbarTop);
    window.addEventListener("scroll", scrollHandler);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateToolbarTop);
      window.removeEventListener("scroll", scrollHandler);
    };
  }, []);

  // âœ… Fetch competitor profiles
  const fetchProfiles = async () => {
    try {
      setLoading(true);
      const workspaceProfiles = await WorkspaceService.fetchWorkspaceProfiles();

      // Filter competitor profiles (own === false)
      const competitorWorkspaceProfiles = workspaceProfiles.filter((p) => p.own === false);

      // ðŸŸ¢ Build createdAt lookup map: id -> createdAt
      const createdAtMap = {};
      competitorWorkspaceProfiles.forEach((profile) => {
        createdAtMap[profile.id] = profile.createdAt;
      });

      // Group IDs by platform
      const grouped = competitorWorkspaceProfiles.reduce((acc, profile) => {
        if (!acc[profile.platform]) acc[profile.platform] = [];
        acc[profile.platform].push(profile.id);
        return acc;
      }, {});

      let profiles = [];

      // Fetch detailed profiles per platform
      for (const platform of Object.keys(grouped)) {
        const ids = grouped[platform];
        if (ids.length > 0) {
          const detailed = await ProfileService.fetchProfile(platform, ids).catch(() => []);
          profiles.push(
            ...detailed.map((p) => ({
              ...p,
              platform,
              createdAt: createdAtMap[p.id] ?? null, // attach createdAt from user mapping
            }))
          );
        }
      }

      // ðŸŸ¢ Sort all profiles by createdAt (newest first)
      profiles.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

      // console.log("âœ… Competitor profiles sorted by createdAt (DESC):", profiles);

      setCompetitorProfiles(profiles);
    } catch (err) {
      console.error("Failed to fetch competitor profiles:", err);
      setCompetitorProfiles([]);
    } finally {
      setLoading(false);
    }
  };



  useEffect(() => {
    fetchProfiles();
  }, []);

  // âœ… Filter logic
  const filteredProfiles = competitorProfiles
    .filter((profile) => !excludeIds.includes(profile.id))
    .filter((profile) => (filterPlatform ? profile.platform === filterPlatform : true))
    .filter((profile) =>
      (profile.name || "").toLowerCase().includes((searchQuery || "").toLowerCase())
    );

  // âœ… Selection logic with 2-profile cap
  const isSelected = (id) => selectedProfiles.some((p) => p.id === id);

  const toggleSelection = (profile) => {
    const alreadySelected = isSelected(profile.id);
    const selectedCount = selectedProfiles.length;

    if (alreadySelected) {
      onSelect(selectedProfiles.filter((p) => p.id !== profile.id));
    } else {
      if (selectedCount >= maxSelection) {
        if (maxSelection === 1) {
          // [UPDATED] Auto-replace if limit is 1
          onSelect([profile]);
          return;
        }
        setSnackbar({ open: false, message: "" });
        setTimeout(() => {
          setSnackbar({ open: true, message: `⚠️ You can select up to ${maxSelection} secondary profiles only` });
        }, 100);
        return;
      }
      onSelect([...selectedProfiles, profile]);
    }
  };

  const handleAddProfileClick = () => setShowSearchModal(true);
  const handleCloseSearchModal = () => {
    setShowSearchModal(false);
    fetchProfiles();
    setSnackbar({ open: true, message: "âœ… Competitor profiles refreshed" });
  };

  return (
    <MDBox>
      {/* Sticky toolbar under stepper */}
      <MDBox
        display="flex"
        flexDirection="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        sx={{
          position: "sticky",
          top: `${toolbarTop}px`,
          zIndex: 999,
          backgroundColor: "#ffffff",
          padding: "5px",
          borderRadius: "8px",
          gap: 2,
          mb: 4,
          boxShadow: isTransparent ? "none" : "0 4px 12px rgba(0, 0, 0, 0.2)",
          transition: "top 0.3s ease, background-color 0.3s ease, box-shadow 0.3s ease",
        }}
      >
        {/* Search */}
        <MDBox flexGrow={1}>
          <ProfileSearch searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
        </MDBox>
        {/* Connect Section */}
        <MDBox display="flex" flexDirection="column" alignItems="center">
          <MDTypography
            variant="button"
            fontWeight="medium"
            color="text"
            sx={{ marginBottom: 1, display: "block", textAlign: "center" }}
          >
            Connect
          </MDTypography>

          <MDBox
            sx={{
              borderRadius: "16px",
              backgroundColor: "#f0f2f5",
              padding: "14px",
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Card
              sx={{
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "row",
                gap: 1,
                padding: "4px 18px",
                borderRadius: "12px",
                backgroundColor: "grey.100",
                boxShadow: 1,
                border: "1px solid #ddd",
                transition: "all 0.3s ease-in-out",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: 4,
                },
                "&:active": {
                  backgroundColor: "grey.400",
                  boxShadow: 6,
                },
              }}
              onClick={handleAddProfileClick}
            >
              <IconButton color="primary" sx={{ p: 0 }}>
                <AddCircleIcon fontSize="large" />
              </IconButton>
              <Typography variant="subtitle2" fontWeight="bold" color="textPrimary" ml={1}>
                Add Competitor Profile
              </Typography>
            </Card>
          </MDBox>
        </MDBox>
      </MDBox>

      {/* âœ… Competitor Cards */}
      <Card>
        <MDBox>
          <MDBox
            mx={2}
            mt={-3}
            py={1}
            px={2}
            variant="gradient"
            bgColor="info"
            borderRadius="lg"
            coloredShadow="info"
            display="flex"
            flexDirection="row"
            alignItems="center"
          >
            <MDTypography variant="h6" color="white">
              Competitor Profiles
            </MDTypography>
            <CustomTooltip title={tooltipContent} placement="bottom" arrow>
              <IconButton sx={{ color: "white", p: 0, ml: 0.5 }}>
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </CustomTooltip>
          </MDBox>

          <MDBox px={2} py={3}>
            {loading ? (
              <MDBox display="flex" justifyContent="center" alignItems="center" p={3}>
                <CircularProgress color="info" />
              </MDBox>
            ) : filteredProfiles.length === 0 ? (
              <MDTypography variant="subtitle1" color="text" align="center">
                No profile found.
              </MDTypography>
            ) : (
              <MDBox
                display="flex"
                flexWrap="wrap"
                justifyContent="center"
                gap={2}
                px={2}
                mt={0}
              >
                {filteredProfiles.map((profile, index) => {

                  // 🟢 DEMO LOGIC: Use passed prop + index/selection check
                  const showHighlight = isDemoHighlight && index === 0 && selectedProfiles.length === 0;

                  return (
                    <Grow
                      in={true}
                      timeout={index * 50}
                      key={profile.id}
                      style={{ transformOrigin: "0 0 0" }}
                    >
                      <MDBox
                        key={profile.id}
                        sx={{
                          flex: "1 1 calc(25% - 16px)",
                          minWidth: "240px",
                          maxWidth: "300px",
                          position: showHighlight ? "relative" : undefined,
                          zIndex: showHighlight ? 1500 : "auto", // High z-index to break out
                          transform: showHighlight ? "scale(1.05)" : undefined,
                          opacity:
                            selectedProfiles.length >= (maxSelection + 1) && !isSelected(profile.id) ? 0.5 : 1,
                          pointerEvents:
                            selectedProfiles.length >= (maxSelection + 1) && !isSelected(profile.id)
                              ? "none"
                              : "auto",
                          transition: "opacity 0.3s ease, transform 0.3s ease",
                          cursor: showHighlight ? "pointer" : "default", // Ensure pointer cursor
                        }}
                      >
                        {showHighlight && (
                          <GuidanceBubble
                            visible={true}
                            message="Click on the profile to select it"
                          />
                        )}

                        <SocialCard
                          platform={profile.platform}
                          profilePic={profile.profilePictureUrl || ""}
                          name={profile.name || "Untitled"}
                          count={profile.count ?? 0}
                          link={profile.link}
                          verifiedStatus={profile.verifiedStatus ?? false}
                          updated_at={profile.updated_at}
                          isSelected={isSelected(profile.id)}
                          onSelect={() => toggleSelection(profile)}
                        />
                      </MDBox>
                    </Grow>
                  );
                })}
              </MDBox>
            )}
          </MDBox>
        </MDBox>
      </Card>

      {/* ðŸ” Add Competitor Modal */}
      <SearchCompetitorModal open={showSearchModal} onClose={handleCloseSearchModal} />

      {/* ðŸŸ¢ Dynamic Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        ContentProps={{
          sx: { borderRadius: 2, boxShadow: 3 },
        }}
      />

    </MDBox>
  );
}

export default SelectSecondaryProfile;
